import React, { useState, useEffect, useRef } from 'react';
import config from '../../../config';
import { Form, notification, Row, Col, Button, Modal, Typography, Upload, Spin, message, Select } from 'antd';
import * as userActions from '../../../redux/actions/userActions';
import { useDispatch } from 'react-redux';
import {
  confirmationModal,
  formItemLayout,
  tailFormItemLayout,
  unexpectedError,
  beforeUpload,
  showUploadFileList,
  showFilesList,
} from '../../../common_functions';
import UploadTrigger from '../../../common_functions/UploadTrigger';
import UploadedCheck from '../../../common_functions/UploadedCheck';

const XRF = props => {
  const dispatch = useDispatch();
  const xrfFormRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [xrf, setXRF] = useState(false);
  const [title, setTitle] = useState(false);
  const [printData, setPrintData] = useState(false);
  const [signatureData, setSignatureData] = useState([]);
  const [dminAreaData, setDminAreaData] = useState([]);
  const [signatureGraph, setSignatureGraph] = useState([]);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [imgPreview, setImgPreview] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [filesState, setFilesState] = useState({ xrf: [] });
  let saveAndNext = '';

  let error = [];
  useEffect(() => {
    setLoading(true);
    async function fetchData() {
      try {
        // Fetch signature_placement and dmin_sample_area data in parallel
        const [signData, dminData] = await Promise.all([
          dispatch(
            userActions.getItems('signature_placement', {
              fields: ['id', 'signature'],
              limit: -1,
            })
          ),
          dispatch(
            userActions.getItems('dmin_sample_area', {
              fields: ['id', 'dmin_sample_area'],
              limit: -1,
            })
          ),
        ]);

        setDminAreaData(dminData.data);
        setSignatureData(signData.data);

        const {
          data: { c_print, collection_id, xrf, micrometer },
        } = await dispatch(
          userActions.getItem('print', props.print, {
            fields: [
              'c_print.acquisition,c_print.title,collection_id.name,xrf.xrf.id,xrf.xrf.filename_download,xrf.id,micrometer.id,xrf.signature_placement,xrf.dmin_sample_area,xrf.signature_graph.filename_download,xrf.signature_graph.id',
            ],
          })
        );
        setPrintData({
          acquisition: c_print[0]['acquisition'],
          title: c_print[0]['title'],
          name: collection_id.name,
        });
        if (xrf.length > 0) {
          const firstXrfItem = xrf[0];
          // Set form values
          setFilesState({ xrf: showUploadFileList(firstXrfItem?.xrf?.id, firstXrfItem?.xrf?.filename_download) });
          firstXrfItem.xrf = firstXrfItem?.xrf?.id;
          const signatureGraph = firstXrfItem?.signature_graph;

          firstXrfItem.signature_graph = firstXrfItem?.signature_graph?.id;
          xrfFormRef.current.setFieldsValue(firstXrfItem);
          setXRF(firstXrfItem);
          if (signatureGraph) {
            const { id, filename_download } = signatureGraph;
            const fileUrl = await dispatch(userActions.getFile(id));
            // Set signature graph state
            setSignatureGraph([
              {
                uid: id,
                name: filename_download,
                url: fileUrl,
              },
            ]);
            // Set image and PDF preview URL
            setImgPreview(fileUrl);
            setPdfPreviewUrl(fileUrl);
          }
        }
        micrometer.length > 0 && setTitle('Edit Micrometer');
      } catch (error) {
        unexpectedError(error);
      }
      setLoading(false);
    }
    //   fetchData();
    // }, [props.print]);
    if (props.activeTab === 'xrf') {
      fetchData();
    }
  }, [props.activeTab]);

  const onRemove = async () => {
    return new Promise((resolve, reject) => {
      xrfFormRef.current.setFields([
        {
          name: 'xrf',
          errors: null,
        },
      ]);
      resolve(true);
    });
  };
  const complete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 1 };
    await onSave(values);
  };
  const incomplete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 0 };
    await onSave(values);
  };
  let onFinish = async (values, action = null) => {
    saveAndNext = '';
    if (!!action) {
      saveAndNext = action;
    }
    await confirmationModal(values, 'Status', 'Is this record complete or incomplete?', 'Incomplete', 'Complete', complete, incomplete);
  };
  // Change Title according to active tab
  let onSave = async values => {
    if (error.length) return false;
    setLoading(true);
    const formData = new FormData();
    const fieldsToAppend = ['object_status', 'signature_placement', 'dmin_sample_area'];
    fieldsToAppend.forEach(key => {
      formData.append(key, values[key]);
    });
    Object.entries(values).forEach(([key, value]) => {
      if (value && value.fileList && value.fileList.length > 0) {
        formData.append(key, value.fileList[0].originFileObj);
      }
    });
    try {
      const response = await dispatch(userActions.saveFileItem('xrf/xrf', formData, props.print, xrf));
      xrfFormRef.current.resetFields('');
      xrfFormRef.current.setFieldsValue(response.data);
      setXRF(response.data);
      if (response.error) {
        notification['error']({
          message: response.error,
          duration: 2,
        });
      } else {
        notification['success']({
          message: 'XRF saved successfully!',
          duration: 2,
        });
        if (saveAndNext == '') {
          setTimeout(() => props.history.push('/print-objects', 1500));
        } else if (saveAndNext == 'next') {
          props.onChange('', 'micrometer', title);
        }
      }
    } catch (error) {
      if (error.errors !== undefined) {
        xrfFormRef.current.setFields([
          {
            name: 'xrf',
            errors: [error.errors],
          },
        ]);
      } else {
        unexpectedError();
      }
    } finally {
      setLoading(false);
    }
  };
  let onFinishFailed = ({ errorFields }) => {
    xrfFormRef.current.scrollToField(errorFields[0].name);
  };
  let backButton = () => {
    props.onChange('', '3', 'Physical Characteristics');
  };
  let fileChange = ({ file, fileList: newFile }, fieldName, filename) => {
    try {
      error = [];
      let correctFileName = `${printData.acquisition.toLowerCase()}.xrf.csv`;
      if (!(file instanceof File)) return;
      if (fieldName === 'xrf') {
        if (file.name.toLowerCase() !== correctFileName) {
          error.push(`Invalid file name. Please ensure the file is named ${correctFileName}`);
        }
        setFilesState(prevState => ({
          ...prevState,
          xrf: newFile,
        }));
      } else {
        // Check the file name
        const allowedFormats = config.FileType.bookImage.toString();
        const fileExtension = file.name.toLowerCase().split('.').pop();
        if (!allowedFormats.includes(file.type)) {
          setLoading(false);
          message.error(`Invalid file format. Please upload a JPEG, JPG, PDF, or PNG file.`);
          return;
        }
        let file_name = `${printData.acquisition}.${filename}.${fileExtension}`;
        if (file.name.toLowerCase() !== file_name.toLowerCase()) {
          error.push(`Please upload a file with the name ${printData.acquisition}.${filename}.${fileExtension}`);
        } else setSignatureGraph(newFile);
      }
      xrfFormRef.current.setFields([
        {
          name: fieldName,
          errors: error.length ? error : '',
        },
      ]);
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const handlePreview = ({ type, thumbUrl, originFileObj, name }) => {
    setImgPreview('');
    if (type === 'application/pdf') {
      setPdfPreviewUrl(URL.createObjectURL(originFileObj));
      setPreviewVisible(true);
    } else {
      setImgPreview(thumbUrl);
    }
    setPreviewTitle(name);
    setPreviewVisible(true);
  };
  const handleCancel = () => setPreviewVisible(false);

  const handleSaveAndNext = action => {
    xrfFormRef.current.validateFields().then(values => {
      onFinish(values, action);
    });
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="white-paper">
          <Row
            style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #0000000f',
              borderRadius: '7px',
              padding: '12px',
            }}
          >
            <Col md={8}>
              <div>Collection</div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <div style={{ fontSize: '19px' }}>{printData.name}</div>
              </Typography.Title>
            </Col>
            <Col md={8}>
              <div>Collection Acquisition ID</div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <div style={{ fontSize: '19px' }}>{printData.acquisition}</div>
              </Typography.Title>
            </Col>
            <Col md={8}>
              <div>Collection Title</div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <div style={{ fontSize: '19px' }}>{printData.title}</div>
              </Typography.Title>
            </Col>
          </Row>
          <div className="artist" style={{ marginTop: '20px' }}>
            <Form
              ref={xrfFormRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
              encType="multipart/form-data"
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="xrf"
                    label={
                      <div>
                        XRF File
                        <UploadedCheck uploaded={xrf?.xrf} />
                      </div>
                    }
                  >
                    <Upload
                      name="file"
                      fileList={filesState.xrf}
                      className="create_object"
                      accept={config.FileType.onlyCSV.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'onlyCSV');
                      }}
                      bookImag
                      onRemove={onRemove}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'xrf')}
                      showUploadList={showFilesList(xrf?.xrf, 'xrf')}
                    >
                      <UploadTrigger fileType="onlyCSV" />
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="signature_placement"
                    label="Signature Read Placement"
                    rules={[
                      {
                        required: true,
                        message: 'Signature read placement is required!',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      placeholder="Select signature read placement"
                      style={{ textTransform: 'capitalize' }}
                    >
                      {signatureData.map(el => (
                        <Select.Option key={el.id} value={el.id}>
                          {el.signature}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="signature_graph" label="Signature Graph">
                    <Upload
                      name="file"
                      className="create_object"
                      listType="picture"
                      accept={config.FileType.bookImage.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'bookImage');
                      }}
                      showUploadList={{
                        showRemoveIcon: xrf?.signature_graph ? false : true,
                      }}
                      fileList={signatureGraph}
                      onPreview={handlePreview}
                      maxCount={1}
                      multiple={false}
                      onChange={e => fileChange(e, 'signature_graph', 'siggraph')}
                    >
                      <UploadTrigger fileType="bookImage" />
                    </Upload>
                  </Form.Item>
                </Col>
                <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel} width={800}>
                  <div style={{ textAlign: 'center' }}>
                    {!!imgPreview ? (
                      <img
                        alt="No Image"
                        style={{
                          width: '100%',
                          height: 'auto',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                        src={imgPreview}
                      />
                    ) : (
                      <iframe
                        src={pdfPreviewUrl}
                        style={{
                          width: '100%',
                          height: '500px',
                          border: 'none',
                        }}
                      />
                    )}
                  </div>
                </Modal>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="dmin_sample_area"
                    label="Dmin Sample Area"
                    rules={[
                      {
                        required: false,
                        message: 'Dmin Sample Area is required!',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      placeholder="Select Dmin sample area"
                      style={{ textTransform: 'capitalize' }}
                    >
                      {dminAreaData.map(el => (
                        <Select.Option key={el.id} value={el.id}>
                          {el.dmin_sample_area}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={backButton}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton()}
                {props.showButton() != '' ? (
                  <Button className="save-btn" type="primary" style={{ margin: '0 8px' }} onClick={() => handleSaveAndNext('next')}>
                    Save and Next
                  </Button>
                ) : (
                  ''
                )}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};
export default XRF;
